import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Theme, themes } from '../lib/themes';
import DeleteConfirmationModal from './DeleteConfirmationModal';

interface Conversation {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  is_pinned?: boolean;
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onToggleCollapse: () => void;
  theme: Theme;
  currentTheme: string;
  onThemeChange: (theme: string) => void;
  conversations: Conversation[];
  currentConversationId?: string;
  onConversationSelect: (conversationId: string) => void;
  onNewChat: () => void;
  onLogout?: () => void;
  onConversationUpdate?: () => void;
  onConversationDelete?: (id: string) => void;
  conversationsLoading?: boolean;
}

export default function Sidebar({ 
  isOpen,
  onClose,
  onToggleCollapse,
  theme, 
  currentTheme, 
  onThemeChange, 
  conversations,
  currentConversationId,
  onConversationSelect,
  onNewChat,
  onLogout,
  onConversationUpdate,
  onConversationDelete,
  conversationsLoading = false,
}: SidebarProps) {
  const [pinnedExpanded, setPinnedExpanded] = useState(true);
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const [themeExpanded, setThemeExpanded] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    conversationId: string | null;
    title: string;
  }>({
    show: false,
    conversationId: null,
    title: '',
  });
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handlePin = async (conversationId: string, isPinned: boolean) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/conversations/${conversationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_pinned: !isPinned }),
      });
      
      if (response.ok) {
        onConversationUpdate?.();
      }
    } catch (error) {
      console.error('Failed to pin conversation:', error);
    }
    setMenuOpenId(null);
  };

  const handleRename = async (conversationId: string) => {
    if (!editTitle.trim()) return;
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/conversations/${conversationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editTitle }),
      });
      
      if (response.ok) {
        onConversationUpdate?.();
      }
    } catch (error) {
      console.error('Failed to rename conversation:', error);
    }
    
    setEditingId(null);
    setEditTitle('');
  };

  const showDeleteConfirmation = (conversationId: string, title: string) => {
    setDeleteConfirmation({
      show: true,
      conversationId,
      title,
    });
    setMenuOpenId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation.conversationId) return;

    // Optimistically remove from list immediately
    onConversationDelete?.(deleteConfirmation.conversationId);
    setDeleteConfirmation({ show: false, conversationId: null, title: '' });

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/conversations/${deleteConfirmation.conversationId}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        if (currentConversationId === deleteConfirmation.conversationId) {
          onNewChat();
        }
        onConversationUpdate?.();
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      // Re-sync on failure
      onConversationUpdate?.();
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation({ show: false, conversationId: null, title: '' });
  };

  const startEdit = (conversation: Conversation) => {
    setEditingId(conversation.id);
    setEditTitle(conversation.title);
    setMenuOpenId(null);
  };

  const pinnedConversations = conversations.filter(c => c.is_pinned);
  const unpinnedConversations = conversations.filter(c => !c.is_pinned);

  const renderConversation = (conversation: Conversation) => {
    const isEditing = editingId === conversation.id;
    const isMenuOpen = menuOpenId === conversation.id;
    
    return (
      <div key={conversation.id} className="relative group">
        <div
          className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-all ${
            currentConversationId === conversation.id 
              ? 'ring-2' 
              : 'hover:bg-white/5'
          }`}
          style={{
            backgroundColor: currentConversationId === conversation.id 
              ? theme.colors.primary + '10' 
              : 'transparent',
            ...(currentConversationId === conversation.id && { 
              '--tw-ring-color': theme.colors.primary 
            } as React.CSSProperties),
          }}
        >
          {isEditing ? (
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRename(conversation.id);
                if (e.key === 'Escape') {
                  setEditingId(null);
                  setEditTitle('');
                }
              }}
              onBlur={() => handleRename(conversation.id)}
              autoFocus
              className="flex-1 bg-transparent border-b outline-none text-sm min-w-0"
              style={{ 
                color: theme.colors.text,
                borderColor: theme.colors.primary
              }}
            />
          ) : (
            <button
              onClick={() => {
                onConversationSelect(conversation.id);
                onClose(); // Close sidebar on mobile after selection
              }}
              className="flex-1 text-left min-w-0 overflow-hidden"
            >
              <p 
                className="text-sm overflow-hidden text-ellipsis whitespace-nowrap" 
                style={{ color: theme.colors.text }}
                title={conversation.title}
              >
                {conversation.title}
              </p>
              <p className="text-xs mt-1 truncate" style={{ color: theme.colors.textSecondary }}>
                {formatDate(conversation.updated_at)}
              </p>
            </button>
          )}
          
          {!isEditing && (
            <div className="relative flex-shrink-0 self-center">
              <button
                ref={(el) => { menuButtonRefs.current[conversation.id] = el; }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (isMenuOpen) {
                    setMenuOpenId(null);
                    setMenuPosition(null);
                  } else {
                    const btn = menuButtonRefs.current[conversation.id];
                    if (btn) {
                      const rect = btn.getBoundingClientRect();
                      setMenuPosition({
                        top: rect.bottom + 4,
                        right: window.innerWidth - rect.right,
                      });
                    }
                    setMenuOpenId(conversation.id);
                  }
                }}
                className="p-1 rounded opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-white/10 transition-opacity"
                style={{ color: theme.colors.textSecondary }}
                aria-label="Conversation options"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };
  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className="fixed inset-y-0 z-50 w-72 flex flex-col transition-[left] duration-300"
        style={{
          left: isOpen ? '0' : '-18rem',
          backgroundColor: theme.colors.surface,
        }}
      >
        {/* Header */}
        <div className="p-4 border-b" style={{ borderColor: theme.colors.border }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <img 
                src="/logo.svg" 
                alt="Project Lazarus" 
                className="w-6 h-6"
              />
              <h2 className="text-xl font-bold" style={{ color: theme.colors.text }}>
                Project Lazarus
              </h2>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={onToggleCollapse}
                className="p-2 rounded-lg hover:bg-white/10"
                style={{ color: theme.colors.textSecondary }}
                title="Close sidebar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            </div>
          </div>

          {/* New Chat Button */}
          <button
            onClick={() => {
              onNewChat();
              onClose(); // Close sidebar on mobile after new chat
            }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all hover:opacity-90"
            style={{
              backgroundColor: theme.colors.primary + '20',
              color: theme.colors.primary,
            }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="font-medium">New chat</span>
          </button>
        </div>

        {/* Chat History */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Pinned Conversations */}
          {pinnedConversations.length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setPinnedExpanded(!pinnedExpanded)}
                className="w-full flex items-center justify-between mb-2 hover:opacity-70 transition-opacity"
              >
                <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: theme.colors.textSecondary }}>
                  Pinned ({pinnedConversations.length})
                </h3>
                <svg 
                  className={`w-4 h-4 transition-transform ${pinnedExpanded ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  style={{ color: theme.colors.textSecondary }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {pinnedExpanded && (
                <div className="space-y-1">
                  {pinnedConversations.map(renderConversation)}
                </div>
              )}
            </div>
          )}

          {/* History Conversations */}
          <div className="mb-4">
            <button
              onClick={() => setHistoryExpanded(!historyExpanded)}
              className="w-full flex items-center justify-between mb-2 hover:opacity-70 transition-opacity"
            >
              <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: theme.colors.textSecondary }}>
                {conversationsLoading ? 'History' : `History (${unpinnedConversations.length})`}
              </h3>
              <svg 
                className={`w-4 h-4 transition-transform ${historyExpanded ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                style={{ color: theme.colors.textSecondary }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {historyExpanded && (
              <>
                {conversationsLoading ? (
                  <div className="space-y-1">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="px-3 py-2 rounded-lg animate-pulse" style={{ opacity: 1 - i * 0.15 }}>
                        <div className="h-3 rounded mb-1.5" style={{ backgroundColor: theme.colors.border, width: `${75 - i * 8}%` }} />
                        <div className="h-2 rounded" style={{ backgroundColor: theme.colors.border, width: '35%' }} />
                      </div>
                    ))}
                  </div>
                ) : unpinnedConversations.length === 0 ? (
                  <p className="text-sm py-4" style={{ color: theme.colors.textSecondary }}>
                    No chat history yet
                  </p>
                ) : (
                  <div className="space-y-1">
                    {unpinnedConversations.map(renderConversation)}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Theme Selector */}
        <div className="border-t" style={{ borderColor: theme.colors.border }}>
          <button
            onClick={() => setThemeExpanded(!themeExpanded)}
            className="w-full flex items-center justify-between px-4 py-3 hover:opacity-70 transition-opacity"
          >
            <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: theme.colors.textSecondary }}>
              Theme
            </h3>
            <svg
              className={`w-4 h-4 transition-transform ${themeExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ color: theme.colors.textSecondary }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {themeExpanded && (
            <div className="px-4 pb-3 overflow-y-auto" style={{ maxHeight: '160px' }}>
              <div className="flex flex-wrap gap-2">
                {Object.entries(themes).map(([key, t]) => (
                  <button
                    key={key}
                    onClick={() => onThemeChange(key)}
                    title={t.name}
                    className="w-8 h-8 rounded-lg flex-shrink-0 transition-transform hover:scale-110 focus:outline-none"
                    style={{
                      background: `linear-gradient(135deg, ${t.colors.primary}, ${t.colors.secondary})`,
                      boxShadow: currentTheme === key
                        ? `0 0 0 2px ${theme.colors.surface}, 0 0 0 4px ${t.colors.primary}`
                        : undefined,
                    }}
                  >
                    {currentTheme === key && (
                      <svg className="w-4 h-4 mx-auto" fill="white" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Logout Button */}
        {onLogout && (
          <div className="p-4 border-t" style={{ borderColor: theme.colors.border }}>
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all hover:bg-red-500/10"
              style={{ color: '#ef4444' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="font-medium">Logout</span>
            </button>
          </div>
        )}
      </div>

      {/* Conversation context menu — rendered via React Portal directly into document.body
          so it escapes the sidebar's stacking context entirely. */}
      {menuOpenId && menuPosition && (() => {
        const conversation = conversations.find(c => c.id === menuOpenId);
        if (!conversation) return null;
        return createPortal(
          <>
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
              onClick={() => { setMenuOpenId(null); setMenuPosition(null); }}
            />
            <div
              style={{
                position: 'fixed',
                zIndex: 9999,
                top: menuPosition.top,
                right: menuPosition.right,
                width: '12rem',
                borderRadius: '0.5rem',
                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.4)',
                border: `1px solid ${theme.colors.border}`,
                overflow: 'hidden',
                backgroundColor: theme.colors.surface,
              }}
            >
              <button
                onClick={() => handlePin(conversation.id, conversation.is_pinned || false)}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-white/5 transition-colors"
                style={{ color: theme.colors.text }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                {conversation.is_pinned ? 'Unpin' : 'Pin'}
              </button>
              <button
                onClick={() => startEdit(conversation)}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-white/5 transition-colors"
                style={{ color: theme.colors.text }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Rename
              </button>
              <button
                onClick={() => showDeleteConfirmation(conversation.id, conversation.title)}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-red-500/10 transition-colors"
                style={{ color: '#ef4444' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </button>
            </div>
          </>,
          document.body
        );
      })()}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={deleteConfirmation.show}
        title="Delete Conversation?"
        message="This action cannot be undone. The conversation will be permanently removed."
        itemPreview={deleteConfirmation.title}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        theme={theme}
      />
    </>
  );
}
